import type {
  BulkMutationResult,
  CreateGuestInput,
  CreateGuestRelationInput,
  Guest,
  GuestCapabilities,
  GuestDuplicateGroup,
  GuestFormOptions,
  GuestImportAction,
  GuestImportPreviewRow,
  GuestImportResult,
  GuestListPage,
  GuestTableOption,
  GuestRelation,
  GuestSummary,
  RsvpResponseValue,
  UpdateGuestInput,
} from '@peles/shared';
import { apiClient } from './client';

/**
 * `/events/:eventId/guests` — an event's guest list, the same routes for the
 * office and for a client. What the caller may do comes back from
 * `capabilities`; the server enforces the same answer.
 */

export interface GuestListQuery {
  page?: number;
  pageSize?: number;
  sort?: 'name' | 'createdAt' | 'table' | 'side' | 'familyGroup' | 'response';
  dir?: 'asc' | 'desc';
  q?: string;
  response?: RsvpResponseValue[];
  side?: string[];
  tags?: string[];
  familyGroup?: string[];
  source?: Array<'PRODUCER' | 'CLIENT' | 'IMPORT' | 'PUBLIC_LINK'>;
  seated?: 'yes' | 'no';
}

export interface GuestBulkEdit {
  response?: RsvpResponseValue;
  side?: string | null;
  familyGroup?: string | null;
  tableNumber?: number | null;
  addTags?: string[];
  removeTags?: string[];
}

export interface GuestRsvpInput {
  response: RsvpResponseValue;
  adultCount?: number;
  kidCount?: number;
  notes?: string | null;
}

const base = (eventId: string) => `/events/${eventId}/guests`;

/** Query params with the empty ones left out, lists joined by commas. */
function toParams(query: GuestListQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
      continue;
    }
    params[key] = Array.isArray(value) ? value.join(',') : value;
  }
  return params;
}

export async function listEventGuests(
  eventId: string,
  query: GuestListQuery = {},
): Promise<GuestListPage> {
  const { data } = await apiClient.get<GuestListPage>(base(eventId), { params: toParams(query) });
  return data;
}

export async function getEventGuestSummary(eventId: string): Promise<GuestSummary> {
  const { data } = await apiClient.get<GuestSummary>(`${base(eventId)}/summary`);
  return data;
}

export async function getEventGuestOptions(eventId: string): Promise<GuestFormOptions> {
  const { data } = await apiClient.get<GuestFormOptions>(`${base(eventId)}/options`);
  return data;
}

export async function getEventGuestCapabilities(eventId: string): Promise<GuestCapabilities> {
  const { data } = await apiClient.get<GuestCapabilities>(`${base(eventId)}/capabilities`);
  return data;
}

export async function createEventGuest(
  eventId: string,
  input: Omit<CreateGuestInput, 'eventId'>,
): Promise<Guest> {
  const { data } = await apiClient.post<Guest>(base(eventId), input);
  return data;
}

export async function updateEventGuest(
  eventId: string,
  guestId: string,
  input: UpdateGuestInput,
): Promise<Guest> {
  const { data } = await apiClient.patch<Guest>(`${base(eventId)}/${guestId}`, input);
  return data;
}

export async function deleteEventGuest(eventId: string, guestId: string): Promise<void> {
  await apiClient.delete(`${base(eventId)}/${guestId}`);
}

export async function setEventGuestRsvp(
  eventId: string,
  guestId: string,
  input: GuestRsvpInput,
): Promise<Guest> {
  const { data } = await apiClient.put<Guest>(`${base(eventId)}/${guestId}/rsvp`, input);
  return data;
}

export async function bulkEditEventGuests(
  eventId: string,
  guestIds: string[],
  updates: GuestBulkEdit,
): Promise<BulkMutationResult> {
  const { data } = await apiClient.post<BulkMutationResult>(`${base(eventId)}/bulk`, {
    guestIds,
    updates,
  });
  return data;
}

export async function bulkDeleteEventGuests(
  eventId: string,
  guestIds: string[],
): Promise<BulkMutationResult> {
  const { data } = await apiClient.post<BulkMutationResult>(`${base(eventId)}/bulk-delete`, {
    guestIds,
  });
  return data;
}

export async function listGuestRelations(eventId: string): Promise<GuestRelation[]> {
  const { data } = await apiClient.get<GuestRelation[]>(`/events/${eventId}/guest-relations`);
  return data;
}

export async function setGuestRelation(
  eventId: string,
  input: CreateGuestRelationInput,
): Promise<GuestRelation> {
  const { data } = await apiClient.post<GuestRelation>(`/events/${eventId}/guest-relations`, input);
  return data;
}

export async function deleteGuestRelation(eventId: string, relationId: string): Promise<void> {
  await apiClient.delete(`/events/${eventId}/guest-relations/${relationId}`);
}

export async function listEventGuestTables(eventId: string): Promise<GuestTableOption[]> {
  const { data } = await apiClient.get<GuestTableOption[]>(`${base(eventId)}/tables`);
  return data;
}

export interface SeatGuestsInput {
  guestIds: string[];
  /** `null` takes the guests off their table. */
  tableNumber: number | null;
  seatNumber?: number;
  /** The producer's yes to a table past its seats. */
  allowOverCapacity?: boolean;
}

export async function seatEventGuests(
  eventId: string,
  input: SeatGuestsInput,
): Promise<{ count: number }> {
  const { data } = await apiClient.post<{ count: number }>(`${base(eventId)}/seat`, input);
  return data;
}

export async function listEventGuestDuplicates(eventId: string): Promise<GuestDuplicateGroup[]> {
  const { data } = await apiClient.get<GuestDuplicateGroup[]>(`${base(eventId)}/duplicates`);
  return data;
}

export async function mergeEventGuests(
  eventId: string,
  keepId: string,
  mergeIds: string[],
): Promise<Guest> {
  const { data } = await apiClient.post<Guest>(`${base(eventId)}/merge`, { keepId, mergeIds });
  return data;
}

export async function previewEventGuestImport(
  eventId: string,
  rows: Array<Record<string, unknown>>,
): Promise<{ rows: GuestImportPreviewRow[] }> {
  const { data } = await apiClient.post<{ rows: GuestImportPreviewRow[] }>(
    `${base(eventId)}/import/preview`,
    { rows },
  );
  return data;
}

export async function commitEventGuestImport(
  eventId: string,
  rows: Array<{ data: Record<string, unknown>; action: GuestImportAction; mergeInto?: string }>,
): Promise<GuestImportResult> {
  const { data } = await apiClient.post<GuestImportResult>(`${base(eventId)}/import/commit`, {
    rows,
  });
  return data;
}
